import type {
  BenefitInputs,
  BenefitRule,
  RuleAction,
  RuleEngineRunResult,
  RuleEvalResult,
} from '../types/rulesEngine'
import {
  buildRuleReason,
  formatDisplayValue,
  humanizeFieldKey,
  lookupBenefitValue,
} from './rulesEngineFields'

const TERMINAL_ACTIONS = new Set<RuleAction>(['approve', 'deny', 'route_prior_auth'])

export type RunRulesEngineOptions = {
  /** When false, every rule is evaluated. Default true (waterfall). */
  stopOnTerminal?: boolean
  fieldLabels?: Record<string, string>
}

export function evaluateRule(rule: BenefitRule, benefits: BenefitInputs): boolean {
  const rawField = lookupBenefitValue(benefits, rule.field)
  if (rawField === undefined || rawField === '') {
    return rule.operator === 'equals' && String(rule.value ?? '') === ''
  }
  const fieldVal = typeof rawField === 'string' ? rawField : Number(rawField)
  let compareVal: string | number = rule.value

  if (Object.prototype.hasOwnProperty.call(benefits, compareVal)) {
    const ref = lookupBenefitValue(benefits, String(compareVal)) ?? benefits[compareVal]
    compareVal = typeof ref === 'string' ? ref : Number(ref)
  } else if (
    rule.operator !== 'equals' &&
    rule.operator !== 'not_equals' &&
    typeof compareVal === 'string' &&
    !Number.isNaN(Number(compareVal))
  ) {
    compareVal = Number(compareVal)
  }

  switch (rule.operator) {
    case 'equals':
      return String(fieldVal) === String(compareVal)
    case 'not_equals':
      return String(fieldVal) !== String(compareVal)
    case 'gt':
      return Number(fieldVal) > Number(compareVal)
    case 'gte':
      return Number(fieldVal) >= Number(compareVal)
    case 'lt':
      return Number(fieldVal) < Number(compareVal)
    case 'lte':
      return Number(fieldVal) <= Number(compareVal)
    default:
      return false
  }
}

function normalizeRules(rules: BenefitRule[]): BenefitRule[] {
  if (!Array.isArray(rules)) return []
  return rules.filter((rule) => rule && typeof rule === 'object')
}

export function evaluateRuleWithDetails(
  rule: BenefitRule,
  benefits: BenefitInputs,
  fieldLabels?: Record<string, string>
): RuleEvalResult {
  const actualRaw = lookupBenefitValue(benefits, rule.field)
  const passed = evaluateRule(rule, benefits)
  const fieldLabel = fieldLabels?.[rule.field] || humanizeFieldKey(rule.field)
  const expectedValue = formatDisplayValue(rule.value)
  const actualValue = formatDisplayValue(actualRaw)
  const reason = buildRuleReason({
    fieldLabel,
    operator: rule.operator,
    expected: expectedValue,
    actual: actualValue,
    passed,
    failMessage: rule.failMessage,
  })

  return {
    rule,
    passed,
    actionTaken: passed ? rule.action : rule.failAction,
    message: passed ? null : rule.failMessage,
    fieldLabel,
    expectedValue,
    actualValue,
    reason,
  }
}

export function runRulesEngine(
  rules: BenefitRule[],
  benefits: BenefitInputs,
  options?: RunRulesEngineOptions
): RuleEngineRunResult {
  const stopOnTerminal = options?.stopOnTerminal !== false
  const results: RuleEngineRunResult['results'] = []
  let finalOutcome: RuleEngineRunResult['finalOutcome'] | null = null

  for (const rule of normalizeRules(rules)) {
    const detail = evaluateRuleWithDetails(rule, benefits, options?.fieldLabels)
    results.push(detail)

    if (stopOnTerminal && TERMINAL_ACTIONS.has(detail.actionTaken)) {
      finalOutcome = {
        action: detail.actionTaken,
        triggerRule: rule.name,
        message: detail.message,
      }
      break
    }
  }

  if (!finalOutcome) {
    const failed = results.filter((r) => !r.passed)
    if (failed.length === 0) {
      finalOutcome = { action: 'approve', triggerRule: 'All rules passed', message: null }
    } else {
      const deny = failed.find((r) => r.actionTaken === 'deny')
      const terminal = failed.find((r) => TERMINAL_ACTIONS.has(r.actionTaken))
      finalOutcome = {
        action: deny?.actionTaken ?? terminal?.actionTaken ?? 'flag',
        triggerRule: deny?.rule.name ?? terminal?.rule.name ?? 'Multiple',
        message: `${failed.length} rule(s) not approved`,
      }
    }
  }

  return { results, finalOutcome }
}
